package fr.manyverse.app.presentation.components.modules.combat

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Casino
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.DragIndicator
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material3.Button
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.zIndex
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.model.CombatParticipantType
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.layout.WindowSizeContainer
import fr.manyverse.app.presentation.layout.WindowWidth
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.combat_add
import manyverse.shared.generated.resources.combat_add_adversary
import manyverse.shared.generated.resources.combat_add_to_combat
import manyverse.shared.generated.resources.combat_adversary_label
import manyverse.shared.generated.resources.combat_adversary_name_hint
import manyverse.shared.generated.resources.combat_drag_handle
import manyverse.shared.generated.resources.combat_in_combat
import manyverse.shared.generated.resources.combat_launch
import manyverse.shared.generated.resources.combat_move_down
import manyverse.shared.generated.resources.combat_move_up
import manyverse.shared.generated.resources.combat_order_empty
import manyverse.shared.generated.resources.combat_order_hint
import manyverse.shared.generated.resources.combat_order_section
import manyverse.shared.generated.resources.combat_players_section
import manyverse.shared.generated.resources.combat_prepare_title
import manyverse.shared.generated.resources.combat_remove_participant
import manyverse.shared.generated.resources.combat_shuffle
import org.jetbrains.compose.resources.stringResource
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Écran plein écran de préparation du combat (MJ).
 * Sélection des personnages joueurs, ajout d'adversaires, définition de l'ordre, lancement.
 */
@OptIn(ExperimentalUuidApi::class)
@Composable
fun CombatPrepareDialog(
    isVisible: Boolean,
    tableMembers: List<TableMember>,
    characters: List<Character>,
    onDismiss: () -> Unit,
    onLaunch: (List<CombatParticipant>) -> Unit,
    modifier: Modifier = Modifier
) {
    if (!isVisible) return

    val players = remember(tableMembers) {
        tableMembers.filter { it.role == MemberRole.PLAYER && it.characterId != null }
    }
    // Liste ordonnée des participants au combat (état local de préparation).
    val order = remember { mutableStateListOf<CombatParticipant>() }
    var adversaryName by remember { mutableStateOf("") }

    fun togglePlayer(member: TableMember) {
        val existingIndex = order.indexOfFirst { it.userId == member.userId }
        if (existingIndex >= 0) order.removeAt(existingIndex)
        else order.add(combatParticipantFor(member, characters))
    }

    fun addAdversary() {
        val name = adversaryName.trim()
        if (name.isEmpty()) return
        order.add(
            CombatParticipant(
                id = Uuid.random().toString(),
                type = CombatParticipantType.ADVERSARY,
                name = name
            )
        )
        adversaryName = ""
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            dismissOnBackPress = false,
            dismissOnClickOutside = false,
            usePlatformDefaultWidth = false
        )
    ) {
        Surface(
            modifier = modifier.fillMaxSize().clearFocusOnTap(),
            color = MaterialTheme.colorScheme.background
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .statusBarsPadding()
                    .padding(16.dp)
            ) {
                // En-tête
                Text(
                    text = stringResource(Res.string.combat_prepare_title),
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold
                )

                Spacer(Modifier.height(12.dp))

                WindowSizeContainer(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                ) { windowWidth ->
                    // En portrait, on empile l'ordre du combat sous la sélection.
                    val isPortrait = windowWidth == WindowWidth.COMPACT
                    val onTogglePlayer: (TableMember) -> Unit = { togglePlayer(it) }
                    val onAddAdversary: () -> Unit = { addAdversary() }
                    val onShuffle: () -> Unit = { order.shuffle() }
                    val onMoveUp: (Int) -> Unit =
                        { index -> if (index > 0) order.add(index - 1, order.removeAt(index)) }
                    val onMoveDown: (Int) -> Unit = { index ->
                        if (index < order.lastIndex) order.add(
                            index + 1,
                            order.removeAt(index)
                        )
                    }
                    val onMove: (Int, Int) -> Unit =
                        { from, to -> order.add(to, order.removeAt(from)) }
                    val onRemoveAt: (Int) -> Unit = { index -> order.removeAt(index) }

                    if (isPortrait) {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .verticalScroll(rememberScrollState()),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            SelectionColumn(
                                modifier = Modifier.fillMaxWidth(),
                                players = players,
                                characters = characters,
                                order = order,
                                adversaryName = adversaryName,
                                onAdversaryNameChange = { adversaryName = it },
                                onTogglePlayer = onTogglePlayer,
                                onAddAdversary = onAddAdversary
                            )
                            OrderColumn(
                                modifier = Modifier.fillMaxWidth(),
                                innerScroll = false,
                                order = order,
                                characters = characters,
                                onShuffle = onShuffle,
                                onMoveUp = onMoveUp,
                                onMoveDown = onMoveDown,
                                onMove = onMove,
                                onRemoveAt = onRemoveAt
                            )
                        }
                    } else {
                        Row(
                            modifier = Modifier.fillMaxSize(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            SelectionColumn(
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxHeight()
                                    .verticalScroll(rememberScrollState()),
                                players = players,
                                characters = characters,
                                order = order,
                                adversaryName = adversaryName,
                                onAdversaryNameChange = { adversaryName = it },
                                onTogglePlayer = onTogglePlayer,
                                onAddAdversary = onAddAdversary
                            )
                            OrderColumn(
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxHeight(),
                                innerScroll = true,
                                order = order,
                                characters = characters,
                                onShuffle = onShuffle,
                                onMoveUp = onMoveUp,
                                onMoveDown = onMoveDown,
                                onMove = onMove,
                                onRemoveAt = onRemoveAt
                            )
                        }
                    }
                }

                Spacer(Modifier.height(12.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    OutlinedButton(
                        onClick = onDismiss,
                        modifier = Modifier
                            .weight(1f)
                            .height(56.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.cancel),
                            style = MaterialTheme.typography.titleMedium
                        )
                    }
                    Button(
                        onClick = { onLaunch(order.toList()) },
                        enabled = order.isNotEmpty(),
                        modifier = Modifier
                            .weight(1f)
                            .height(56.dp)
                    ) {
                        Icon(Icons.Default.PlayArrow, contentDescription = null)
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = stringResource(Res.string.combat_launch),
                            style = MaterialTheme.typography.titleMedium
                        )
                    }
                }
            }
        }
    }
}

private fun combatParticipantFor(
    member: TableMember,
    characters: List<Character>
): CombatParticipant {
    val character = characters.firstOrNull { it.userId == member.userId }
    return CombatParticipant(
        id = member.userId,
        type = CombatParticipantType.CHARACTER,
        name = character?.name ?: member.userId.take(8),
        userId = member.userId,
        characterId = member.characterId
    )
}

/** Sélection des personnages joueurs + ajout d'adversaires. */
@Composable
private fun SelectionColumn(
    modifier: Modifier,
    players: List<TableMember>,
    characters: List<Character>,
    order: List<CombatParticipant>,
    adversaryName: String,
    onAdversaryNameChange: (String) -> Unit,
    onTogglePlayer: (TableMember) -> Unit,
    onAddAdversary: () -> Unit
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        SectionCard {
            Text(
                text = stringResource(Res.string.combat_players_section),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
            players.forEach { member ->
                val inCombat = order.any { it.userId == member.userId }
                PlayerRow(
                    participant = combatParticipantFor(member, characters),
                    characters = characters,
                    inCombat = inCombat,
                    onToggle = { onTogglePlayer(member) }
                )
            }
        }

        SectionCard {
            Text(
                text = stringResource(Res.string.combat_add_adversary),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
            OutlinedTextField(
                value = adversaryName,
                onValueChange = onAdversaryNameChange,
                placeholder = { Text(stringResource(Res.string.combat_adversary_name_hint)) },
                singleLine = true,
                keyboardOptions = KeyboardOptions(
                    capitalization = KeyboardCapitalization.Sentences,
                    imeAction = ImeAction.Done
                ),
                keyboardActions = KeyboardActions(onDone = { onAddAdversary() }),
                modifier = Modifier.fillMaxWidth()
            )
            OutlinedButton(
                onClick = onAddAdversary,
                enabled = adversaryName.isNotBlank(),
                modifier = Modifier.fillMaxWidth()
            ) {
                Icon(Icons.Default.PlayArrow, contentDescription = null)
                Spacer(Modifier.width(8.dp))
                Text(stringResource(Res.string.combat_add_to_combat))
            }
        }
    }
}

/**
 * Conteneur visuel d'un bloc : fond légèrement plus clair que l'arrière-plan
 * et fine bordure discrète pour délimiter clairement chaque section.
 */
@Composable
private fun SectionCard(
    modifier: Modifier = Modifier,
    verticalArrangement: Arrangement.Vertical = Arrangement.spacedBy(10.dp),
    content: @Composable ColumnScope.() -> Unit
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .background(
                MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f),
                RoundedCornerShape(16.dp)
            )
            .border(
                1.dp,
                MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f),
                RoundedCornerShape(16.dp)
            )
            .padding(12.dp),
        verticalArrangement = verticalArrangement,
        content = content
    )
}

/** Ordre du combat : réordonnancement, suppression, mélange. */
@Composable
private fun OrderColumn(
    modifier: Modifier,
    innerScroll: Boolean,
    order: List<CombatParticipant>,
    characters: List<Character>,
    onShuffle: () -> Unit,
    onMoveUp: (Int) -> Unit,
    onMoveDown: (Int) -> Unit,
    onMove: (Int, Int) -> Unit,
    onRemoveAt: (Int) -> Unit
) {
    // État du glisser-déposer : id du participant déplacé + décalage vertical accumulé.
    var draggingId by remember { mutableStateOf<String?>(null) }
    var dragOffset by remember { mutableStateOf(0f) }
    var rowHeightPx by remember { mutableStateOf(0f) }

    SectionCard(modifier = modifier, verticalArrangement = Arrangement.Top) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = stringResource(Res.string.combat_order_section),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f)
            )
            TextButton(
                onClick = onShuffle,
                enabled = order.size > 1
            ) {
                Icon(Icons.Default.Casino, contentDescription = null)
                Spacer(Modifier.width(4.dp))
                Text(stringResource(Res.string.combat_shuffle))
            }
        }
        Text(
            text = stringResource(Res.string.combat_order_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Spacer(Modifier.height(8.dp))

        if (order.isEmpty()) {
            Box(
                modifier = if (innerScroll) Modifier.fillMaxSize() else Modifier.fillMaxWidth()
                    .height(120.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = stringResource(Res.string.combat_order_empty),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        } else {
            val listModifier = if (innerScroll) {
                Modifier.weight(1f).verticalScroll(rememberScrollState())
            } else {
                Modifier.fillMaxWidth()
            }
            Column(
                modifier = listModifier,
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                order.forEachIndexed { index, participant ->
                    // key stable : la ligne déplacée garde son composable (et son geste de
                    // drag) au lieu d'être recyclée par position lors d'un réordonnancement.
                    key(participant.id) {
                        val isDragging = participant.id == draggingId
                        OrderRow(
                            order = index + 1,
                            participant = participant,
                            characters = characters,
                            canMoveUp = index > 0,
                            canMoveDown = index < order.lastIndex,
                            isDragging = isDragging,
                            onMoveUp = { onMoveUp(index) },
                            onMoveDown = { onMoveDown(index) },
                            onRemove = { onRemoveAt(index) },
                            modifier = Modifier
                                .zIndex(if (isDragging) 1f else 0f)
                                .graphicsLayer { translationY = if (isDragging) dragOffset else 0f }
                                .onGloballyPositioned { rowHeightPx = it.size.height.toFloat() },
                            // Drag immédiat depuis la poignée (fiable souris + tactile, cf. desktop).
                            dragHandleModifier = Modifier.pointerInput(participant.id) {
                                detectDragGestures(
                                    onDragStart = {
                                        draggingId = participant.id
                                        dragOffset = 0f
                                    },
                                    onDragEnd = {
                                        draggingId = null
                                        dragOffset = 0f
                                    },
                                    onDragCancel = {
                                        draggingId = null
                                        dragOffset = 0f
                                    },
                                    onDrag = { change, amount ->
                                        change.consume()
                                        dragOffset += amount.y
                                        val current = order.indexOfFirst { it.id == draggingId }
                                        val slot = rowHeightPx + 8.dp.toPx()
                                        if (slot > 0f && current >= 0) {
                                            when {
                                                dragOffset > slot / 2 && current < order.lastIndex -> {
                                                    onMove(current, current + 1)
                                                    dragOffset -= slot
                                                }

                                                dragOffset < -slot / 2 && current > 0 -> {
                                                    onMove(current, current - 1)
                                                    dragOffset += slot
                                                }
                                            }
                                        }
                                    }
                                )
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PlayerRow(
    participant: CombatParticipant,
    characters: List<Character>,
    inCombat: Boolean,
    onToggle: () -> Unit
) {
    val containerColor = if (inCombat) MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f)
    else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(containerColor, RoundedCornerShape(14.dp))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        CombatParticipantAvatar(participant = participant, characters = characters, size = 44.dp)
        Text(
            text = participant.name,
            style = MaterialTheme.typography.titleMedium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f)
        )
        if (inCombat) {
            Button(onClick = onToggle) { Text(stringResource(Res.string.combat_in_combat)) }
        } else {
            OutlinedButton(onClick = onToggle) { Text(stringResource(Res.string.combat_add)) }
        }
    }
}

@Composable
private fun OrderRow(
    order: Int,
    participant: CombatParticipant,
    characters: List<Character>,
    canMoveUp: Boolean,
    canMoveDown: Boolean,
    isDragging: Boolean,
    onMoveUp: () -> Unit,
    onMoveDown: () -> Unit,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier,
    dragHandleModifier: Modifier = Modifier
) {
    val containerColor = if (isDragging) MaterialTheme.colorScheme.surfaceVariant
    else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
    val borderColor = if (isDragging) MaterialTheme.colorScheme.primary
    else MaterialTheme.colorScheme.outlineVariant
    Row(
        modifier = modifier
            .fillMaxWidth()
            .shadow(if (isDragging) 8.dp else 0.dp, RoundedCornerShape(14.dp))
            .background(containerColor, RoundedCornerShape(14.dp))
            .border(if (isDragging) 2.dp else 1.dp, borderColor, RoundedCornerShape(14.dp))
            .padding(horizontal = 10.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Box(
            modifier = dragHandleModifier.size(40.dp),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                imageVector = Icons.Default.DragIndicator,
                contentDescription = stringResource(Res.string.combat_drag_handle),
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        OrderBadge(order = order, isActive = false)
        CombatParticipantAvatar(participant = participant, characters = characters, size = 40.dp)
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = participant.name,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            if (participant.type == CombatParticipantType.ADVERSARY) {
                Text(
                    text = stringResource(Res.string.combat_adversary_label),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            IconButton(
                onClick = onMoveUp,
                enabled = canMoveUp,
                modifier = Modifier.size(32.dp)
            ) {
                Icon(
                    Icons.Default.KeyboardArrowUp,
                    contentDescription = stringResource(Res.string.combat_move_up)
                )
            }
            IconButton(
                onClick = onMoveDown,
                enabled = canMoveDown,
                modifier = Modifier.size(32.dp)
            ) {
                Icon(
                    Icons.Default.KeyboardArrowDown,
                    contentDescription = stringResource(Res.string.combat_move_down)
                )
            }
        }
        FilledIconButton(
            onClick = onRemove,
            modifier = Modifier.size(36.dp),
            colors = IconButtonDefaults.filledIconButtonColors(
                containerColor = MaterialTheme.colorScheme.errorContainer
            )
        ) {
            Icon(
                Icons.Default.Delete,
                contentDescription = stringResource(Res.string.combat_remove_participant),
                tint = MaterialTheme.colorScheme.onErrorContainer,
                modifier = Modifier.size(18.dp)
            )
        }
    }
}

